import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { adminUsersApi } from '../api/admin-users.api';
import { useSetupPassword } from '../hooks/useSetupPassword';
import { setupPasswordSchema, type SetupPasswordFormValues } from '../schemas/admin-user-schema';
import { publicPaths } from '@/app/router/paths/publicPaths';
import appIcon from '@/assets/icons/icon.svg';
import { getErrorMessage } from '@/core/api/error-messages';
import type { ApiError, ErrorState, PageStatus } from '@/features/auth/types/page.type';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

/**
 * SetupPasswordPage - Handles password setup for new admin users
 *
 * Flow:
 * 1. New admin receives email with setup link
 * 2. Admin is redirected to /admin/setup-password?token=...
 * 3. This page validates the token with the backend API
 * 4. If valid, admin enters new password (minimum 8 characters)
 * 5. Password is created via backend API and account is activated
 */
export default function SetupPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasValidated = useRef(false);
  const [status, setStatus] = useState<PageStatus>('validating');
  const [errorState, setErrorState] = useState<ErrorState | null>(null);
  const [validatedToken, setValidatedToken] = useState<string | null>(null);
  const { mutate: setupPassword, isPending: isSubmitting } = useSetupPassword();

  const form = useForm<SetupPasswordFormValues>({
    resolver: zodResolver(setupPasswordSchema),
    defaultValues: {
      token: '',
      password: '',
      confirmPassword: '',
    },
  });

  // Validate the setup token on mount
  useEffect(() => {
    // Prevent double processing in React StrictMode
    if (hasValidated.current) return;
    hasValidated.current = true;

    const validateToken = async () => {
      // Get token from URL query params
      const token = searchParams.get('token');

      if (!token) {
        setStatus('invalid');
        setErrorState({
          title: 'Token inválido',
          message: 'El enlace de configuración de contraseña es inválido.',
        });
        return;
      }

      try {
        // Validate token with backend API
        await adminUsersApi.validateToken(token);
        setStatus('ready');
        setValidatedToken(token);
        form.setValue('token', token);
      } catch (error: unknown) {
        console.error('Error validating token:', error);

        // Get error code from response
        const apiError = error as ApiError;
        const errorCode = apiError?.response?.data?.code || apiError?.response?.data?.type;
        const statusCode = apiError?.response?.status;

        // Get appropriate error message
        const errorMsg = getErrorMessage(errorCode, statusCode);

        setStatus('error');
        setErrorState({
          title: errorMsg.title,
          message: errorMsg.message,
        });
      }
    };

    validateToken();
  }, [searchParams, form]);

  const onSubmit = async (values: SetupPasswordFormValues) => {
    setStatus('submitting');

    setupPassword(
      {
        token: validatedToken!,
        password: values.password,
      },
      {
        onSuccess: () => {
          // Password setup successful!
          setStatus('success');
        },
        onError: (error: unknown) => {
          console.error('Error setting up password:', error);

          const apiError = error as ApiError;
          const errorCode = apiError?.response?.data?.code || apiError?.response?.data?.type;
          const statusCode = apiError?.response?.status;

          // Get appropriate error message
          const errorMsg = getErrorMessage(errorCode, statusCode);

          setStatus('ready');
          setErrorState({
            title: errorMsg.title,
            message: errorMsg.message,
          });
        },
      },
    );
  };

  const goToLogin = () => {
    navigate(publicPaths.LOGIN);
  };

  if (status === 'validating') {
    return <div className="w-screen h-screen" aria-hidden />;
  }

  // Invalid token state (no token in URL)
  if (status === 'invalid') {
    return (
      <div className="w-screen h-screen flex justify-center items-center">
        <div className="flex flex-col justify-center items-center p-0 gap-10 max-w-md w-full px-4">
          <div className="flex flex-col gap-4">
            <img src={appIcon} alt="Login Logo" />
            <div className="flex flex-col items-center p-0 gap-2">
              <XCircle className="w-16 h-16 text-negative" />
              <p className="text-welcome-subtitle text-base">
                {errorState?.title || 'Token inválido'}
              </p>
            </div>
          </div>
          <div className="w-full flex flex-col items-flex-start gap-6">
            <p className="text-sm text-center text-welcome-subtitle">
              {errorState?.message ||
                'El enlace de configuración de contraseña es inválido o ha expirado.'}
            </p>
          </div>
          <div className="flex w-full flex-col items-center gap-4">
            <UnifiedButton
              buttonType="prominent"
              size="prominent-lg"
              className="w-full"
              onClick={goToLogin}
            >
              Ir a inicio de sesión
            </UnifiedButton>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="w-screen h-screen flex justify-center items-center">
        <div className="flex flex-col justify-center items-center p-0 gap-10 max-w-md w-full px-4">
          <div className="flex flex-col gap-4">
            <img src={appIcon} alt="Login Logo" />
            <div className="flex flex-col items-center p-0 gap-2">
              <XCircle className="w-16 h-16 text-negative" />
              <p className="text-welcome-subtitle text-base">{errorState?.title || 'Error'}</p>
            </div>
          </div>
          <div className="w-full flex flex-col items-flex-start gap-6">
            <p className="text-sm text-center text-welcome-subtitle">
              {errorState?.message || 'Ocurrió un error al procesar la solicitud.'}
            </p>
          </div>
          <div className="flex w-full flex-col items-center gap-4">
            <UnifiedButton
              buttonType="prominent"
              size="prominent-lg"
              className="w-full"
              onClick={goToLogin}
            >
              Ir a inicio de sesión
            </UnifiedButton>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="w-screen h-screen flex justify-center items-center">
        <div className="flex flex-col justify-center items-center p-0 gap-10 max-w-md w-full px-4">
          <div className="flex flex-col gap-4">
            <img src={appIcon} alt="Login Logo" />
            <div className="flex flex-col items-center p-0 gap-2">
              <CheckCircle className="w-16 h-16 text-positive" />
              <p className="text-welcome-subtitle text-base">Cuenta activada</p>
            </div>
          </div>
          <div className="w-full flex flex-col items-flex-start gap-6">
            <p className="text-sm text-center text-welcome-subtitle">
              Tu cuenta ha sido activada exitosamente. Por favor, inicia sesión con tu nueva
              contraseña.
            </p>
          </div>
          <div className="flex w-full flex-col items-center gap-4">
            <UnifiedButton
              buttonType="prominent"
              size="prominent-lg"
              className="w-full"
              onClick={goToLogin}
            >
              Ir a iniciar sesión
            </UnifiedButton>
          </div>
        </div>
      </div>
    );
  }

  // Ready state - show password form
  return (
    <div className="w-screen h-screen flex justify-center items-center">
      <div className="flex flex-col justify-center items-center p-0 gap-10 max-w-md w-full px-4">
        <div className="flex flex-col gap-6">
          <img src={appIcon} alt="Login Logo" />
          <div className="flex flex-col items-center p-0 gap-2">
            <p className="text-welcome-subtitle text-base">Configura tu contraseña</p>
            <p className="text-welcome-subtitle text-xs text-center">
              Crea una contraseña segura para activar tu cuenta de administrador.
            </p>
          </div>
        </div>

        {/* Show inline error if password setup failed */}
        {errorState && (
          <div className="w-full p-3 bg-negative/10 border border-negative/30 rounded-md">
            <p className="text-sm text-negative text-center">{errorState.message}</p>
          </div>
        )}

        <div className="w-full flex flex-col items-flex-start gap-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6 w-full">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Contraseña (mínimo 8 caracteres)"
                        type="password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input placeholder="Confirmar contraseña" type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex w-full flex-col items-center gap-4">
                <UnifiedButton
                  buttonType="prominent"
                  size="prominent-lg"
                  className="w-full"
                  type="submit"
                  disabled={isSubmitting || status === 'submitting'}
                >
                  {isSubmitting || status === 'submitting'
                    ? 'Activando cuenta...'
                    : 'Activar cuenta'}
                </UnifiedButton>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
